<template>
    <v-form v-model="formData.isValid">
        <v-row>
            <v-col cols="12">
                <p>Start- und Zielflughafen des Fluges (Hinweis: hier bitte die ganze einheitlich gebuchte Verbindung angeben)</p>
                <vuetify-google-autocomplete
                    id="start"
                    label="Startflughafen"
                    types="airport"
                    v-on:placechanged="setStartAddress"
                    :value="flightData.start"
                    :rules="validationRules.defaultNotNull"
                />
                <vuetify-google-autocomplete
                    id="end"
                    label="Zielflughafen"
                    types="airport"
                    v-on:placechanged="setEndAddress"
                    :value="flightData.end"
                    :rules="validationRules.defaultNotNull"
                />
            </v-col>
            <v-col cols="12" md="4" offset-md="4">
                <v-btn rounded v-model="viewData.hasStopover" @click.stop="toogleStopover" color="primary" block>
                    {{ stopoverButtonText }}
                </v-btn>
            </v-col>
            <v-col cols="12" v-if="viewData.hasStopover">
                <vuetify-google-autocomplete
                    id="stopover"
                    label="Zwischenstopp"
                    types="airport"
                    v-on:placechanged="setStopoverAddress"
                    :value="flightData.stopover"
                    :rules="validationRules.defaultNotNull"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <p>Die Flugverbindung...</p>
                <v-radio-group v-model="flightData.type" :rules="validationRules.defaultNotNull">
                    <v-radio v-for="caseType in CaseTypes.toArray()" :key="caseType.value" :label="caseType.title" :value="caseType.value"/>
                </v-radio-group>
            </v-col>
            <v-col cols="12">
                <p>Datum des Fluges</p>
                <v-menu
                    ref="flightDateMenu"
                    v-model="viewData.showFlightDateMenu"
                    :close-on-content-click="false"
                    :return-value.sync="flightData.flightdate"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                >
                    <template v-slot:activator="{on}">
                        <v-text-field
                            :value="formattedFlightDate"
                            readonly
                            label="Flugdatum"
                            v-on="on"
                            :rules="validationRules.defaultNotNull"
                        />
                    </template>
                    <v-date-picker
                        locale="de"
                        no-title
                        scrollable
                        v-model="flightData.flightdate"
                        :max="viewData.maxFlightdate"
                    >
                        <v-spacer/>
                        <v-btn text color="secondary" @click="viewData.showFlightDateMenu = false">Abbrechen</v-btn>
                        <v-btn text color="primary" @click="$refs.flightDateMenu.save(flightData.flightdate)">OK</v-btn>
                    </v-date-picker>
                </v-menu>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <p>{{ plannedRelevantDateLabel }}</p>
                <v-datetime-picker
                    clear-text="Löschen"
                    date-format="dd.MM.yyyy"
                    :text-field-props="dateTimeField.textFieldProps"
                    :time-picker-props="dateTimeField.datetimeTimeProps"
                    :date-picker-props="dateTimeField.datetimeDateProps"
                    v-model="plannedRelevantDate"
                >
                    <template slot="dateIcon">
                        <v-icon>mdi-calendar</v-icon>
                    </template>
                    <template slot="timeIcon">
                        <v-icon>mdi-clock-outline</v-icon>
                    </template>
                </v-datetime-picker>
            </v-col>
            <v-col cols="12">
                <p>{{ actualRelevantDateLabel }}</p>
                <v-datetime-picker
                    clear-text="Löschen"
                    date-format="dd.MM.yyyy"
                    :text-field-props="{rules: [actualRelevantDateRules]}"
                    :time-picker-props="dateTimeField.datetimeTimeProps"
                    :date-picker-props="actualRelevantDateDatePickerAttributes"
                    v-model="actualRelevantDate"
                    :disabled="viewData.hasNotArrived"
                    ref="actualRelevantDateField"
                >
                    <template slot="dateIcon">
                        <v-icon>mdi-calendar</v-icon>
                    </template>
                    <template slot="timeIcon">
                        <v-icon>mdi-clock-outline</v-icon>
                    </template>
                </v-datetime-picker>
                <v-checkbox
                    v-if="flightData.type !== CaseTypes.CASE_TYPE_NO_BOARDING.value"
                    v-model="viewData.hasNotArrived"
                    label="Ziel wurde nicht erreicht (da kein Ersatzflug angetreten wurde)"
                    :rules="[actualRelevantDateRules]"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <p>{{ mainAirlineNameFieldText }}</p>
                <v-select
                    v-model="flightData.main_airline_id"
                    item-value="id"
                    item-text="name"
                    :items="airlines"
                    persistent-hint
                    hint="Hier die Fluglinie eintragen, die den ursprünglich annullierten, verspäteten oder vorverlegten Flug
                        durchgeführt hat oder durchführen sollte (wichtig wenn der Flug über eine andere
                        Fluglinie gebucht wurde)."
                    :rules="validationRules.selectField"
                />
            </v-col>
            <v-col cols="12">
                <p>{{ mainFlightnumberFieldText }}</p>
                <v-text-field
                    v-model="flightData.flightnumber_main"
                    :rules="validationRules.flightnumber"
                    :persistent-hint="showFlightnumberMainHint"
                    :hint="flightnumberMainHint"
                />
            </v-col>
        </v-row>
        <v-row v-if="viewData.hasStopover">
            <v-col cols="12">
                <p>Fluglinie 2 (Zwischenstopp - Ziel)</p>
                <v-select
                    v-model="flightData.sub_airline_id"
                    item-value="id"
                    item-text="name"
                    :items="airlines"
                    persistent-hint
                    hint="Hier die Fluglinie eintragen, die den ursprünglich annullierten, verspäteten oder vorverlegten Flug
                        durchgeführt hat oder durchführen sollte (wichtig wenn der Flug über eine andere
                        Fluglinie gebucht wurde)."
                    :rules="validationRules.selectField"
                />
            </v-col>
            <v-col cols="12">
                <p>Flugnummer 2 (Zwischenstopp - Ziel)</p>
                <v-text-field
                    v-model="flightData.flightnumber_sub"
                    :rules="validationRules.flightnumber"
                    :persistent-hint="showFlightnumberSubHint"
                    :hint="flightnumberSubHint"
                />
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import CaseTypes from "@/constants/CaseTypes";
import {isNotNullOrUndefined} from "@/helper/TypeHelper";
import moment from "moment";
import {isNotNull, requiresSelect} from "@/helper/ValidationHelper";

export default {
    name: 'FlightData',
    emits: ['validity-changed', 'form-data-changed'],
    props: {
        airlines: {
            type: Array,
            default: function()
            {
                return [];
            }
        }
    },
    computed: {
        CaseTypes()
        {
            return CaseTypes;
        },
        stopoverButtonText()
        {
            if (this.viewData.hasStopover)
                return 'Zwischenstopp entfernen';

            return 'Zwischenstopp hinzufügen';
        },
        actualRelevantDateLabel()
        {
            if (this.flightData.type === CaseTypes.CASE_TYPE_FORWARDED.value)
                return 'Tatsächliche Abflugszeit am Startflughafen';
            else
                return 'Tatsächliche Ankunftszeit am Zielflughafen';
        },
        plannedRelevantDateLabel()
        {
            if (this.flightData.type === CaseTypes.CASE_TYPE_FORWARDED.value)
                return 'Geplante Abflugszeit am Startflughafen';
            else
                return 'Geplante Ankunftszeit am Zielflughafen';
        },
        mainAirlineNameFieldText()
        {
            if (this.viewData.hasStopover) {
                return 'Fluglinie 1 (Start - Zwischenstopp)';
            }
            return 'Fluglinie';
        },
        mainFlightnumberFieldText()
        {
            if (this.viewData.hasStopover) {
                return 'Flugnummer 1 (Start - Zwischenstopp)';
            }
            return 'Flugnummer';
        },
        formattedFlightDate()
        {
            if (isNotNullOrUndefined(this.flightData.flightdate))
                return moment(this.flightData.flightdate).format('D.M.YYYY');

            return null;
        },
        plannedRelevantDate:
        {
            get: function ()
            {
                if (isNotNullOrUndefined(this.flightData.planned_relevant_date))
                    return moment(this.flightData.planned_relevant_date).toDate();

                return null;
            },
            set: function (newValue)
            {
                this.flightData.planned_relevant_date = moment(newValue).toISOString(true);
            }
        },
        actualRelevantDate:
        {
            get: function ()
            {
                if (isNotNullOrUndefined(this.flightData.actual_relevant_date))
                    return moment(this.flightData.actual_relevant_date).toDate();

                return null;
            },
            set: function (newValue)
            {
                if (newValue === null)
                    this.flightData.actual_relevant_date = null;
                else
                    this.flightData.actual_relevant_date = moment(newValue).toISOString(true);
            }
        },
        actualRelevantDateRules()
        {
            if(this.flightData.type === CaseTypes.CASE_TYPE_NO_BOARDING.value)
            {
                if(this.actualRelevantDate === null)
                    return 'Bitte gib die tatsächliche Ankunftszeit an';
            }
            else
            {
                if(!this.viewData.hasNotArrived && this.actualRelevantDate === null)
                    return 'Bitte gib die tatsächliche Ankunftszeit an oder wähle "Ziel wurde nicht erreicht" aus';
            }

            return true;
        },
        actualRelevantDateDatePickerAttributes()
        {
            let pickerAttributes = {
                locale: 'de-de',
                'first-day-of-week': '1',
                min: null,
                max: null
            };

            if(this.flightData.type === CaseTypes.CASE_TYPE_FORWARDED.value)
                pickerAttributes.max = this.flightData.planned_relevant_date
            else if(this.flightData.type === CaseTypes.CASE_TYPE_CANCELED.value)
                pickerAttributes.max = moment().toISOString();
            else
            {
                pickerAttributes.min = this.flightData.planned_relevant_date;
                pickerAttributes.max = moment().toISOString();
            }

            return pickerAttributes;
        },
        showFlightnumberMainHint()
        {
            return this.flightnumberMainHint !== null;
        },
        showFlightnumberSubHint()
        {
            return this.flightnumberSubHint !== null;
        },
        flightnumberMainHint()
        {
            let me = this;
            let hintText = null;
            let isFlightNumberPossible = false;
            let airline = this.getAirlineById(this.flightData.main_airline_id);

            if(isNotNullOrUndefined(airline) && isNotNullOrUndefined(this.flightData.flightnumber_main) && this.flightData.flightnumber_main !== '')
            {
                airline.codes.forEach(function(codeData)
                {
                    if(me.flightData.flightnumber_main.startsWith(codeData))
                        isFlightNumberPossible = true;
                });

                if(!isFlightNumberPossible)
                {
                    hintText = 'Achtung: Die von dir eingebene Flugnummer beginnt nicht mit dem üblichen Kürzel dieser Fluglinie (';
                    hintText += airline.codes.join(', ');
                    hintText += '). Bitte überprüf z.B. anhand der Bordkarte, ob der Flug  nicht von einer anderen Fluglinie durchgeführt wurde und gib in diesem Fall diese Fluglinie ein.';
                }
            }

            return hintText;
        },
        flightnumberSubHint()
        {
            let me = this;
            let hintText = null;
            let isFlightNumberPossible = false;
            let airline = this.getAirlineById(this.flightData.sub_airline_id);

            if(isNotNullOrUndefined(airline) && isNotNullOrUndefined(this.flightData.flightnumber_sub) && this.flightData.flightnumber_sub !== '')
            {
                airline.codes.forEach(function(codeData)
                {
                    if(me.flightData.flightnumber_sub.startsWith(codeData))
                        isFlightNumberPossible = true;
                });

                if(!isFlightNumberPossible)
                {
                    hintText = 'Achtung: Die von dir eingebene Flugnummer beginnt nicht mit dem üblichen Kürzel dieser Fluglinie (';
                    hintText += airline.codes.join(', ');
                    hintText += '). Bitte überprüf z.B. anhand der Bordkarte, ob der Flug  nicht von einer anderen Fluglinie durchgeführt wurde und gib in diesem Fall diese Fluglinie ein.';
                }
            }

            return hintText;
        }
    },
    data: () => ({
        viewData: {
            hasStopover: false,
            hasNotArrived: false,
            maxFlightdate: moment().toISOString()
        },
        formData: {
            isValid: false
        },
        validationRules: {
            defaultNotNull: [
                isNotNull
            ],
            selectField: [
                requiresSelect
            ],
            flightnumber: [
                isNotNull,
                v => /^[0-z]*$/.test(v) || 'Bitte gib die Flugnummer nur mit Buchstaben und Zahlen sowie ohne Leerzeichen ein.'
            ]
        },
        flightData: {
            start: null,
            start_longitude: null,
            start_latitude: null,
            start_country: null,
            end: null,
            end_longitude: null,
            end_latitude: null,
            end_country: null,
            stopover: null,
            type: null,
            flightdate: null,
            planned_relevant_date: null,
            actual_relevant_date: null,
            main_airline_id: null,
            sub_airline_id: null,
            flightnumber_main: null,
            flightnumber_sub: null
        },
        dateTimeField: {
            textFieldProps: {
                rules: [
                    isNotNull
                ]
            },
            datetimeTimeProps: {
                format: '24hr'
            },
            datetimeDateProps: {
                locale: 'de-de',
                'first-day-of-week': '1',
                max: moment().toISOString()
            }
        }
    }),
    methods: {
        toogleStopover()
        {
            this.viewData.hasStopover = !this.viewData.hasStopover;
        },
        getCountryShortname(addrComponents)
        {
            // extract country short name (e.g. GB for Great Britain) from google geocode API result
            // see https://stackoverflow.com/questions/19248012/json-google-geocode-api-get-country
            for (let i = 0; i < addrComponents.length; i++)
            {
                if (addrComponents[i].types[0] == 'country')
                    return addrComponents[i].short_name;
            }

            return false;
        },
        setStartAddress(addressData, placeResultData)
        {
            this.resetStartAddress();

            this.flightData.start = placeResultData.name + ', ' + placeResultData.formatted_address;
            this.flightData.start_country = this.getCountryShortname(placeResultData.address_components);
            this.flightData.start_latitude = addressData.latitude;
            this.flightData.start_longitude = addressData.longitude;
        },
        resetStartAddress: function ()
        {
            this.flightData.start = null;
            this.flightData.start_country = null;
            this.flightData.start_latitude = null;
            this.flightData.start_longitude = null;
        },
        setEndAddress: function (addressData, placeResultData)
        {
            this.resetEndAddress();

            this.flightData.end = placeResultData.name + ', ' + placeResultData.formatted_address;
            this.flightData.end_country = this.getCountryShortname(placeResultData.address_components);
            this.flightData.end_latitude = addressData.latitude;
            this.flightData.end_longitude = addressData.longitude;
        },
        resetEndAddress: function ()
        {
            this.flightData.end = null;
            this.flightData.end_country = null;
            this.flightData.end_latitude = null;
            this.flightData.end_longitude = null;
        },
        setStopoverAddress: function (addressData, placeResultData)
        {
            this.flightData.stopover = placeResultData.name + ', ' + placeResultData.formatted_address;
        },
        getAirlineById(id)
        {
            return this.airlines.find((airline) => airline.id === id);
        }
    },
    watch: {
        'viewData.hasStopover': function (newValue)
        {
            if (newValue === false)
            {
                this.flightData.stopover = null;
                this.flightData.sub_airline_id = null;
                this.flightData.flightnumber_sub = null;
            }
        },
        'viewData.hasNotArrived': function (newValue)
        {
            if (newValue)
                this.$refs.actualRelevantDateField.clearHandler();
        },
        'formData.isValid': function(newValue)
        {
            this.$emit('validity-changed', newValue);
        },
        'flightData.type': function(newValue, oldValue)
        {
            if (newValue === CaseTypes.CASE_TYPE_NO_BOARDING.value)
                this.viewData.hasNotArrived = false;

            if(newValue === CaseTypes.CASE_TYPE_FORWARDED.value || oldValue === CaseTypes.CASE_TYPE_FORWARDED.value || oldValue === CaseTypes.CASE_TYPE_CANCELED.value)
                this.$refs.actualRelevantDateField.clearHandler();
        },
        flightData:
        {
            handler: function(flightData)
            {
                this.$emit('form-data-changed', flightData);
            },
            deep: true
        }
    }
}
</script>

<style scoped>

</style>
